import React, { useEffect, useState } from "react";
import {
  Card,
  Grid,
  CardContent,
  Typography,
  TextField,
  Button,
  Box,
  Chip,
  Paper,
  FormControl,
  Autocomplete,
} from "@mui/material";
import { CustomPaper } from "components/Styles/globalStyles";
import {
  inputStyle,
  StyledCard,
  HeaderBox,
  GrossButton,
  CumulativeChip,
} from "components/StyledComponents/styledComponents";
import PropTypes from "prop-types";
import { StyledButton } from "components/StyledComponents/styledComponents";

const ExtendedPopover = ({ selectedEntries, bankAccounts, onProceed, setAlert }) => {
  const [extendedData, setExtendedData] = useState(
    selectedEntries.reduce(
      (acc, entry) => ({
        ...acc,
        [entry.disburseId]: { percent: 0, amount: 0 },
      }),
      {}
    )
  );
  const MInput = inputStyle();
  const [selectedBank, setSelectedBank] = useState({
    actledId: 0,
    actledName: "",
  });
  const [payload, setPayload] = useState(null);
  const [formattedCode, setFormattedCode] = useState(null);

  const updateCalculation = (id, type, value) => {
    const entry = selectedEntries.find((e) => e.disburseId === id);
    const disbursed = entry.disbursedAmount;
    const initialGross = entry.grossPayoutAmount;
    const initialPercent = entry.grossPayoutPercent;

    let extendedPercent = type === "percent" ? parseFloat(value) || 0 : extendedData[id].percent;
    let extendedAmount = type === "amount" ? parseFloat(value) || 0 : extendedData[id].amount;

    if (type === "percent") {
      extendedAmount = (extendedPercent / 100) * disbursed;
    } else if (type === "amount") {
      extendedPercent = (extendedAmount / disbursed) * 100;
    }

    setExtendedData((prev) => ({
      ...prev,
      [id]: { percent: extendedPercent, amount: extendedAmount },
    }));
  };
  const handleBankChange = (event, newValue) => {
    setSelectedBank(newValue);
  };

  const generatePayload = () => {
    const payload = selectedEntries.map((entry) => {
      const id = entry.disburseId;
      const extended = extendedData[id];
      const initialGross = entry.grossPayoutAmount;
      const initialPercent = entry.grossPayoutPercent;
      const cumulativePercent = initialPercent + extended.percent;
      const cumulativeAmount = initialGross + extended.amount;
      const tdsFinalGrossPayout = cumulativeAmount - cumulativeAmount * 0.02;
      return {
        disburseId: id,
        applicationNumber: entry.applicationNumber,
        customerName: entry.customerName,
        disbursedAmount: entry.disbursedAmount,
        extendedPercent: extended.percent,
        extendedAmount: Math.round(extended.amount, 2),
        cumulativeGrossPayoutPercent: cumulativePercent,
        cumulativeGrossPayoutAmount: Math.round(cumulativeAmount, 2),
        tdsFinalGrossPayout: Math.round(tdsFinalGrossPayout, 2),
        ...entry,
      };
    });
    if (!selectedBank?.actledId) {
      setAlert("Please Select Bank Account", "error");
      return;
    }
    const payload2 = payload.map((item) => ({
      disburseId: item.disburseId,
      date: new Date().toISOString().split("T")[0],
      dsa_Id: item.dsaId,
      invoiceDisplayNumber: item.invoices,
      customerAccountId: item.bankerActledId,
      dsaBankActledId: selectedBank.actledId,
      finalGrossPayoutAmount: item.cumulativeGrossPayoutAmount,
      finalGrossPayoutPerecent: item.cumulativeGrossPayoutPercent,
      extendedGrosspayoutPercent: item.extendedPercent,
      extendedGrossPayoutAmount: item.extendedAmount,
      tdsFinalGrossPayout: item.tdsFinalGrossPayout,
    }));

    setPayload(payload);
    setFormattedCode(payload2);
    onProceed(payload2);
  };

  return (
    <Box className="container mx-auto p-6 max-w-5xl bg-gray-100 min-h-screen mt-2">
      <Grid container spacing={3} justifyContent="center">
        {selectedEntries.map((entry) => {
          const id = entry.disburseId;
          const cumulativePercent = (entry.grossPayoutPercent + extendedData[id].percent).toFixed(
            2
          );
          const cumulativeAmount = Math.round(entry.grossPayoutAmount + extendedData[id].amount);

          return (
            <Grid item xs={12} sm={6} md={4} key={id}>
              <StyledCard key={id}>
                <HeaderBox>
                  <Grid container spacing={3} justifyContent="center">
                    <Grid item xs={8} key={id}>
                      <Box className="space-y-2">
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          className="font-semibold text-gray-700"
                        >
                          Application Number:{" "}
                          <span className="text-gray-600">{entry.applicationNumber}</span>
                        </Typography>
                        <Typography variant="body2" className="font-semibold text-gray-700">
                          Customer Name: <span className="text-gray-600">{entry.customerName}</span>
                        </Typography>
                        <Typography variant="body2" className="font-semibold text-gray-700">
                          Disbursed Amount:{" "}
                          <span className="text-gray-600" style={{ fontWeight: "bold" }}>
                            ₹{entry.disbursedAmount.toLocaleString()}
                          </span>
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={4} key={id}>
                      <Box display="flex" flexDirection="column" alignItems="flex-end" gap={1}>
                        <CumulativeChip label={`${cumulativePercent}%`} />
                        <GrossButton disableRipple>
                          ₹{cumulativeAmount.toLocaleString()}
                        </GrossButton>
                      </Box>
                    </Grid>
                  </Grid>
                </HeaderBox>
                <CardContent className="space-y-0">
                  <Grid container spacing={2} justifyContent="center">
                    <Grid item xs={6} key={id}>
                      <Box className="flex items-center gap-4">
                        <Typography variant="caption" sx={{ fontWeight: 600, minWidth: "80px" }}>
                          Extended %:
                        </Typography>
                        <TextField
                          type="number"
                          size="small"
                          placeholder="e.g., 0.1"
                          inputProps={{ step: "0.01" }}
                          value={extendedData[id].percent || ""}
                          onChange={(e) => updateCalculation(id, "percent", e.target.value)}
                          className="flex-1"
                          InputLabelProps={{
                            sx: {
                              color: MInput.a,
                              "&.Mui-focused": {
                                color: "red",
                              },
                            },
                          }}
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={6} key={id}>
                      <Box className="flex items-center gap-4">
                        <Typography variant="caption" sx={{ fontWeight: 600, minWidth: "80px" }}>
                          Extended Amount:
                        </Typography>
                        <TextField
                          type="number"
                          size="small"
                          placeholder="e.g., 10000"
                          inputProps={{ step: "100" }}
                          value={extendedData[id].amount || ""}
                          onChange={(e) => updateCalculation(id, "amount", e.target.value)}
                          className="flex-1"
                          InputLabelProps={{
                            sx: {
                              color: MInput.a,
                              "&.Mui-focused": {
                                color: "red",
                              },
                            },
                          }}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </StyledCard>
            </Grid>
          );
        })}
      </Grid>
      <Box
        sx={{
          borderTop: "1px solid #e5e7eb",
          p: 2,
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <FormControl sx={{ minWidth: 100, width: "100%", maxWidth: "200px", marginRight: "10px" }}>
          <Autocomplete
            options={bankAccounts}
            getOptionLabel={(option) => `${option.actledName}`}
            value={selectedBank}
            onChange={handleBankChange}
            PaperComponent={CustomPaper}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Bank"
                sx={{
                  height: 43,
                  ...MInput.b,
                  "& .MuiInputBase-root": {
                    height: "100%",
                    minHeight: 43,
                  },
                }}
                InputLabelProps={{
                  sx: {
                    color: MInput.a,
                    "&.Mui-focused": {
                      color: "red",
                    },
                  },
                }}
              />
            )}
          />
        </FormControl>
        <StyledButton onClick={generatePayload}>Proceed</StyledButton>
      </Box>
      {payload && (
        <Grid container spacing={2} justifyContent="center">
          <Grid item xs={6} key="grid1">
            <Paper elevation={3} className="mt-6 p-6">
              <Typography variant="h6" className="font-semibold text-gray-800 mb-3">
                Generated Payload Array:
              </Typography>
              <pre className="bg-white p-4 rounded-lg shadow-inner text-sm text-gray-700 overflow-auto">
                {JSON.stringify(payload, null, 2)}
              </pre>
            </Paper>
          </Grid>
          <Grid item xs={6} key="grid2">
            <Paper elevation={3} className="mt-6 p-6">
              <Typography variant="h6" className="font-semibold text-gray-800 mb-3">
                Generated Payload Array:
              </Typography>
              <pre className="bg-white p-4 rounded-lg shadow-inner text-sm text-gray-700 overflow-auto">
                {JSON.stringify(formattedCode, null, 2)}
              </pre>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default ExtendedPopover;

ExtendedPopover.propTypes = {
  selectedEntries: PropTypes.arrayOf(PropTypes.object).isRequired,
  onProceed: PropTypes.func.isRequired,
  bankAccounts: PropTypes.arrayOf(PropTypes.object).isRequired,
  setAlert: PropTypes.func.isRequired,
};
